'use client';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Cross-provider auth-sync bridge — relocated from the frontend into the
 * SDK as part of the auth cutover (see the plan §Target end-state). Pure
 * React (refs + two state cells) — no vendor / DOM / platform coupling, so
 * it lives in `src/auth/` core and ships to web AND React Native.
 *
 * It is the signalling channel between the Turnkey integration and the
 * SDK login flow: a fresh Turnkey login is buffered here (`setFreshLogin`
 * / `consumeFreshLogin`), OAuth profile claims are stashed across the
 * redirect, the Turnkey logout + `completeOauth` callbacks are registered
 * here, and session-expiry is fanned out to the registered logout fn.
 */
import { createContext, useCallback, useContext, useRef, useState, } from 'react';
// ── Context ──────────────────────────────────────────────────────────────────
const AuthSyncContext = createContext(null);
/** Returns the auth-sync bridge. Throws outside `<AuthSyncProvider>`. */
export function useAuthSync() {
    const ctx = useContext(AuthSyncContext);
    if (ctx === null) {
        throw new Error('useAuthSync must be used within AuthSyncProvider');
    }
    return ctx;
}
// ── Provider ─────────────────────────────────────────────────────────────────
/**
 * Cross-provider bridge for auth-state coordination. Mounted ABOVE the
 * Turnkey integration + the SDK login flow so both communicate without a
 * direct dependency. Mostly refs (signalling); two state cells where a
 * consumer must re-render (`connectFailure`, `freshLoginVersion`).
 */
export function AuthSyncProvider({ children }) {
    // A deliberate imperative signalling bridge — mostly refs, with two rarely
    // -changing state cells. The React Compiler conservatively rejects passing
    // ref-reading callbacks through a context value; opt this provider out (it
    // barely re-renders and does not need compiler memoization).
    'use no memo';
    const freshLoginRef = useRef(null);
    const oauthProfileRef = useRef(null);
    const pendingConnectRef = useRef(null);
    const turnkeyLogoutRef = useRef(null);
    const completeOauthRef = useRef(null);
    const pendingCompleteOauthRef = useRef(null);
    const [connectFailure, setConnectFailureState] = useState(null);
    const [freshLoginVersion, setFreshLoginVersion] = useState(0);
    const oauthLoginStartedListenersRef = useRef(new Set());
    const oauthLoginFailedListenersRef = useRef(new Set());
    const logoutRef = useRef(null);
    const setOAuthProfile = useCallback((profile) => {
        oauthProfileRef.current = profile;
    }, []);
    const consumeOAuthProfile = useCallback(() => {
        const profile = oauthProfileRef.current;
        oauthProfileRef.current = null;
        return profile;
    }, []);
    const setFreshLogin = useCallback((data) => {
        freshLoginRef.current = data;
        setFreshLoginVersion((v) => v + 1);
    }, []);
    const signalOauthLoginStarted = useCallback(() => {
        for (const fn of oauthLoginStartedListenersRef.current)
            fn();
    }, []);
    const subscribeOauthLoginStarted = useCallback((fn) => {
        oauthLoginStartedListenersRef.current.add(fn);
        return () => {
            oauthLoginStartedListenersRef.current.delete(fn);
        };
    }, []);
    const signalOauthLoginFailed = useCallback(() => {
        for (const fn of oauthLoginFailedListenersRef.current)
            fn();
    }, []);
    const subscribeOauthLoginFailed = useCallback((fn) => {
        oauthLoginFailedListenersRef.current.add(fn);
        return () => {
            oauthLoginFailedListenersRef.current.delete(fn);
        };
    }, []);
    const peekFreshLogin = useCallback(() => freshLoginRef.current, []);
    const consumeFreshLogin = useCallback(() => {
        const data = freshLoginRef.current;
        freshLoginRef.current = null;
        return data;
    }, []);
    const setPendingConnect = useCallback((data) => {
        pendingConnectRef.current = data;
    }, []);
    const peekPendingConnect = useCallback(() => pendingConnectRef.current, []);
    const consumePendingConnect = useCallback(() => {
        const data = pendingConnectRef.current;
        pendingConnectRef.current = null;
        return data;
    }, []);
    const registerTurnkeyLogout = useCallback((fn) => {
        turnkeyLogoutRef.current = fn;
    }, []);
    // Counts in-flight `completeOauth` exchanges; a buffered (not-yet-run)
    // exchange also counts as "completing" via `pendingCompleteOauthRef`.
    const completingOauthCountRef = useRef(0);
    const registerCompleteOauth = useCallback((fn) => {
        completeOauthRef.current = fn;
        const buffered = pendingCompleteOauthRef.current;
        if (buffered !== null) {
            pendingCompleteOauthRef.current = null;
            completingOauthCountRef.current += 1;
            void fn(buffered).finally(() => {
                completingOauthCountRef.current -= 1;
            });
        }
    }, []);
    const runCompleteOauth = useCallback(async (params) => {
        const fn = completeOauthRef.current;
        if (fn === null) {
            pendingCompleteOauthRef.current = params;
            return;
        }
        completingOauthCountRef.current += 1;
        try {
            await fn(params);
        }
        finally {
            completingOauthCountRef.current -= 1;
        }
    }, []);
    const isCompletingOauth = useCallback(() => completingOauthCountRef.current > 0 || pendingCompleteOauthRef.current !== null, []);
    const oauthConnectCaptureRef = useRef(null);
    const beginOauthConnectCapture = useCallback(() => new Promise((resolve, reject) => {
        oauthConnectCaptureRef.current = { resolve, reject };
    }), []);
    const isCapturingOauthConnect = useCallback(() => oauthConnectCaptureRef.current !== null, []);
    const resolveOauthConnectCapture = useCallback((capture) => {
        const pending = oauthConnectCaptureRef.current;
        if (pending !== null) {
            oauthConnectCaptureRef.current = null;
            pending.resolve(capture);
        }
    }, []);
    const cancelOauthConnectCapture = useCallback(() => {
        const pending = oauthConnectCaptureRef.current;
        if (pending !== null) {
            oauthConnectCaptureRef.current = null;
            pending.reject(new Error('oauth connect capture cancelled'));
        }
    }, []);
    const setConnectFailure = useCallback((reason) => {
        setConnectFailureState(reason);
    }, []);
    const clearConnectFailure = useCallback(() => {
        setConnectFailureState(null);
    }, []);
    const turnkeyLogout = useCallback(async () => {
        if (turnkeyLogoutRef.current !== null) {
            try {
                await turnkeyLogoutRef.current();
            }
            catch {
                // SILENT-CATCH: Turnkey logout failure is non-critical — the Morfi
                // session is already revoked; IndexedDB will eventually expire.
            }
        }
    }, []);
    const registerLogout = useCallback((fn) => {
        logoutRef.current = fn;
    }, []);
    const signalSessionExpired = useCallback(() => {
        if (logoutRef.current !== null) {
            void logoutRef.current();
        }
    }, []);
    const value = {
        setFreshLogin,
        peekFreshLogin,
        consumeFreshLogin,
        setOAuthProfile,
        consumeOAuthProfile,
        setPendingConnect,
        peekPendingConnect,
        consumePendingConnect,
        beginOauthConnectCapture,
        isCapturingOauthConnect,
        resolveOauthConnectCapture,
        cancelOauthConnectCapture,
        registerTurnkeyLogout,
        turnkeyLogout,
        registerCompleteOauth,
        runCompleteOauth,
        isCompletingOauth,
        connectFailure,
        setConnectFailure,
        clearConnectFailure,
        signalSessionExpired,
        registerLogout,
        freshLoginVersion,
        signalOauthLoginStarted,
        subscribeOauthLoginStarted,
        signalOauthLoginFailed,
        subscribeOauthLoginFailed,
    };
    return _jsx(AuthSyncContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=auth-sync.js.map